import React, { createContext, useContext, useCallback, useState } from 'react';
import DashboardService from "../pages/Dashboard/dashboard.service";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
    const [cartState, setCartState] = useState({
        cartData: [],
        Prioritizes: [],
        DistanceFormula: 0,
        AddressessList: [],
        UserDetails: null,
        UserBalance: null,
        Calculations: {},
        DeliveryMethods: [],
    });
    const [loading, setLoading] = useState(false);

    const setAllFromResponse = useCallback((data) => {
        if (!data) return;
        setCartState({
            cartData: data.cartData || [],
            Prioritizes: data.Prioritizes || [],
            DistanceFormula: data.DistanceFormula || 0,
            AddressessList: data.AddressessList || [],
            UserDetails: data.UserDetails?.[0] || null,
            UserBalance: data.UserBalance?.[0] || null,
            Calculations: data.Calculations || {},
            DeliveryMethods: data.DeliveryMethods || [],
        });
    }, []);

    const fetchCartData = useCallback(async () => {
        try {
            setLoading(true);
            const userString = localStorage.getItem('user');
            const user = JSON.parse(userString || '{}');
            const userId = user?._id;
            if (!userId) {
                setAllFromResponse({ cartData: [] });
                return;
            }
            const updateData = {
                functionName: "GetCartList",
                postData: {
                    user_id: userId,
                    filters: {
                        status: false,
                        coupan_code: '',
                        prioritize: "",
                        DeliveryMethod: '',
                        address_id: '',
                        self_pickup: false,
                        quantity: { cart_id: '', quantity: '' }
                    }
                }
            };
            const response = await DashboardService.GetCartList(updateData);
            setAllFromResponse(response?.data || {});
        } catch (e) {
            // noop
        } finally {
            setLoading(false);
        }
    }, [setAllFromResponse]);

    const updateQuantity = useCallback(async (cartId, quantity, options = {}) => {
        try {
            setLoading(true);
            const userId = cartState.UserDetails?._id || JSON.parse(localStorage.getItem('user') || '{}')?._id;
            const payload = {
                functionName: "GetCartList",
                postData: {
                    user_id: userId,
                    filters: {
                        status: true,
                        coupan_code: options.coupan_code || '',
                        DeliveryMethod: options.DeliveryMethod || '',
                        address_id: options.address_id || '',
                        self_pickup: options.self_pickup ?? true,
                        prioritize: options.prioritize || 0,
                        quantity: { cart_id: cartId, quantity }
                    }
                }
            };
            const response = await DashboardService.GetCartList(payload);
            setAllFromResponse(response?.data || {});
        } catch (e) {
            // noop
        } finally {
            setLoading(false);
        }
    }, [cartState.UserDetails, setAllFromResponse]);

    const removeItem = useCallback(async (cartId, options = {}) => {
        return updateQuantity(cartId, '', options);
    }, [updateQuantity]);

    const emptyCart = useCallback(async () => {
        try {
            setLoading(true);
            const userString = localStorage.getItem('user');
            const user = JSON.parse(userString || '{}');
            const userId = user?._id;
            const updateData = { functionName: "DeleteCart", postData: { user_id: userId, cart_id: "" } };
            const response = await DashboardService.DeleteCart(updateData);
            // After successful delete, reflect empty state
            setAllFromResponse(response?.data || { cartData: [] });
            // Double-sync from backend for safety
            await fetchCartData();
        } catch (e) {
            // noop
        } finally {
            setLoading(false);
        }
    }, [fetchCartData, setAllFromResponse]);

    const value = {
        cartState,
        loading,
        setAllFromResponse,
        fetchCartData,
        updateQuantity,
        removeItem,
        emptyCart,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);


